import DOMPurify from "dompurify";

/**
 * A descrição do produto em HTML: o que entra, o que sai, e como o texto
 * antigo continua funcionando.
 *
 * O cadastro tem um editor (negrito, listas, links) e grava HTML. A loja
 * renderiza esse HTML com `dangerouslySetInnerHTML`, e o nome do atributo é
 * honesto: sem limpar antes, um `<script>` gravado por uma conta de admin
 * comprometida rodaria em toda página de produto, para todo visitante. O
 * servidor já limpa ao gravar (HtmlSeguro.php); aqui é a segunda camada, para
 * a loja não depender de o banco estar limpo.
 *
 * A lista de tags espelha a do servidor de propósito. Se um dia o editor
 * ganhar tabela, os dois lados mudam juntos — senão o que o admin vê no editor
 * some silenciosamente na loja.
 */
export const TAGS_PERMITIDAS = ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "h2", "h3", "blockquote", "a"];
export const ATRIBUTOS_PERMITIDOS = ["href", "target", "rel"];

/* Todo link com `target` sai com `rel="noopener noreferrer"`, tenha vindo com
   isso ou não. É o que impede a página de destino de mexer na aba da loja
   pelo `window.opener` — e é por esse risco que o DOMPurify remove `target`
   por padrão; permitir de volta sem o `rel` seria abrir o buraco de novo. */
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS: TAGS_PERMITIDAS,
    ALLOWED_ATTR: ATRIBUTOS_PERMITIDOS,
    // `target` precisa ser pedido à parte: o DOMPurify o barra por padrão
    // mesmo listado acima (ver o hook).
    ADD_ATTR: ["target"],
    /* Só http(s) e mailto como esquema de link. A forma é a da regex padrão do
       DOMPurify com a lista de esquemas encurtada — e a forma importa: ela é
       aplicada a TODO valor de atributo, não só ao href. Um `^https?:` seco
       derrubava `target="_blank"` e `rel`, porque "_blank" não é URL. O
       `[^a-z]` e o ramo final são o que deixam passar valor que não tem
       esquema nenhum; `javascript:` e `data:` continuam barrados. */
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  });
}

/**
 * Parece HTML? Decide qual caminho a descrição toma.
 *
 * Produto cadastrado antes do editor tem texto puro com quebras de linha; o
 * novo tem `<p>`. Uma marcação de abertura de tag é o suficiente para separar
 * os dois — texto de vitrine não usa `<` por conta própria.
 */
export function looksHtml(texto) {
  return /<[a-z][^>]*>/i.test(String(texto || ""));
}

const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
const escapar = (s) => s.replace(/[&<>"]/g, (c) => ESCAPES[c]);

/**
 * Texto puro → HTML, preservando o que a pessoa escreveu.
 *
 * É o que deixa o produto antigo abrir no editor novo já formatado: linha em
 * branco vira parágrafo, Enter simples vira `<br>`. Sem isto, o editor
 * receberia "linha1\nlinha2" e mostraria tudo numa linha — e o admin acharia
 * que a descrição foi corrompida ao trocar de editor.
 */
export function plainToHtml(texto) {
  const t = String(texto || "").replace(/\r\n?/g, "\n").trim();
  if (!t) return "";
  return t
    .split(/\n{2,}/)
    .map((par) => `<p>${escapar(par).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/** Descrição pronta para a loja, venha do editor ou do cadastro antigo. */
export function descricaoParaHtml(descricao) {
  return sanitizeHtml(looksHtml(descricao) ? descricao : plainToHtml(descricao));
}

/**
 * Descrição pronta para o EDITOR: HTML entra como está, texto puro é convertido.
 * Distinta da função da loja porque aqui não se limpa — o editor já só emite
 * o que conhece, e limpar na entrada apagaria a formatação enquanto se digita.
 */
export function descricaoParaEditor(descricao) {
  return looksHtml(descricao) ? String(descricao) : plainToHtml(descricao);
}

/**
 * O que o editor emite, pronto para gravar.
 *
 * O Tiptap mantém um parágrafo vazio no FIM do documento enquanto se edita
 * (é onde a pessoa clica para continuar escrevendo depois de uma lista ou de
 * um título). Bom para editar, lixo para gravar: cada salvamento adicionaria
 * um `<p></p>` à descrição, e a loja desenharia uma linha em branco a mais no
 * pé. Sai aqui, na fronteira — o editor continua com o parágrafo de apoio.
 */
export function htmlDoEditor(html) {
  const semRabo = String(html || "").replace(/(?:<p>(?:\s|<br\s*\/?>)*<\/p>)+$/i, "");
  return isEmptyHtml(semRabo) ? "" : semRabo;
}

/**
 * O editor devolve `<p></p>` quando a pessoa apaga tudo. Para o formulário e
 * para o servidor, isso é "sem descrição" — não um parágrafo vazio a gravar.
 */
export function isEmptyHtml(html) {
  return String(html || "").replace(/<p>(\s|<br\s*\/?>|&nbsp;)*<\/p>/gi, "").trim() === "";
}
